import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../context/AppContext";
import sellerService from "../services/sellerService";
import { PaymentMethod } from "../models/SellerModel";
import paymentMethodService from "../services/paymentMethodService";
import styles from "../pages/StoreSettingsPage.module.css";

interface EditPaymentMethodsCompProps {
    show: boolean;
    onClose: () => void;
}

interface PaymentMethodsFormErrors {
    paymentMethods?: string;
    paymentTerms?: string;
}

const EditPaymentMethodsComp: React.FC<EditPaymentMethodsCompProps> = ({
    show,
    onClose,
}) => {
    const resourceURL = process.env.REACT_APP_RESOURCE_URL;
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller, setSeller } = useAppContext();
    const [globalPaymentMethods, setGlobalPaymentMethods] = useState<PaymentMethod[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(seller!.paymentMethods);
    const [paymentTerms, setPaymentTerms] = useState(seller!.paymentTerms);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethodsFormErrors, setPaymentMethodsFormErrors] = useState<PaymentMethodsFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    const getGlobalPaymentMethods = async () => {
        try {
            const response = await paymentMethodService.getAvailablePaymentMethods();
            setGlobalPaymentMethods(response);
        } catch (error) {
            console.error("Failed to fetch global payment methods", error);
        }
    }

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setPaymentMethods(seller!.paymentMethods);
            setPaymentTerms(seller!.paymentTerms);
            setServiceStatus({ type: '', message: '' });
            getGlobalPaymentMethods();
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

   

    const handlePaymentMethodsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });

        
        setIsSubmitting(true);

        try {
            const form = e.currentTarget as HTMLFormElement;
            const formData = new FormData(form);
            const selectedPaymentMethodIds = formData.getAll("paymentMethods").map(id => Number(id));
            const selectedPaymentMethods = globalPaymentMethods.filter(method =>
                selectedPaymentMethodIds.includes(method.paymentMethodId)
            );
            const updateSucceed = await sellerService.updateSellerPaymentMethods(selectedPaymentMethodIds, paymentTerms);
            if (updateSucceed === "Success") {
                setSeller(prev => ({
                    ...prev!,
                    paymentMethods: selectedPaymentMethods,
                    paymentTerms: paymentTerms
                }));

                onClose();
            } else {
                setServiceStatus({ type: "error", message: "Failed to update payment methods. Please try again." });
            }
        } catch (error) {
            setServiceStatus({ type: "error", message: "Failed to update payment methods. Please try again." });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-lg">
                <div className="modal-content rounded-0">
                    <form onSubmit={handlePaymentMethodsSubmit}>
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Payment Method</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => bsModalRef.current?.hide()}
                                disabled={isSubmitting}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label">Payment Accepted</label>
                                    <div className="d-flex flex-row flex-wrap  align-items-center gap-4 py-2">
                                        {globalPaymentMethods && globalPaymentMethods.length > 0 && globalPaymentMethods.map((method) => {
                                            const isChecked = seller?.paymentMethods?.some(
                                                (pm) => pm.paymentMethodId === method.paymentMethodId
                                            );

                                            return (
                                                <div
                                                    key={method.paymentMethodId}
                                                    className="d-flex align-items-center mb-2"
                                                    style={{ minWidth: "100px" }}
                                                >

                                                    <div className="d-flex flex-column  align-items-center">
                                                        <div className="form-check d-flex align-items-center ">
                                                            <input
                                                                type="checkbox"
                                                                className="me-2 form-check-input"
                                                                defaultChecked={isChecked}
                                                                name="paymentMethods"
                                                                value={method.paymentMethodId}
                                                            />
                                                            <img
                                                                src={`${resourceURL}/images/payment_methods/${method.iconImage}`}
                                                                alt={method.name}
                                                                className={`${styles.paymentMethodIcon} me-2`}
                                                            />
                                                        </div>

                                                        <span className="text-value small">{method.name}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {paymentMethodsFormErrors.paymentMethods && <div className="invalid-feedback">{paymentMethodsFormErrors.paymentMethods}</div>}
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Payment Terms</label>
                                    <textarea
                                        className={`form-control ${paymentMethodsFormErrors.paymentTerms ? 'is-invalid' : ''}`}
                                        rows={6}
                                        value={paymentTerms}
                                        onChange={(e) => setPaymentTerms(e.target.value)}
                                        disabled={isSubmitting}
                                    ></textarea>
                                    {paymentMethodsFormErrors.paymentTerms && <div className="invalid-feedback">{paymentMethodsFormErrors.paymentTerms}</div>}
                                </div>
                            </div>

                            {serviceStatus.type.toLowerCase() === "error" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                            {serviceStatus.type.toLowerCase() === "success" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-dark rounded-0"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-dark rounded-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditPaymentMethodsComp;
